import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { UserProfileDtoV2, UsersServiceV2, UserResponseDtoV2, RolesServiceV2 } from 'api-client';
import { catchError, map, of, switchMap } from 'rxjs';
import { ImpersonationStore } from 'shared';

@Component({
  selector: 'app-impersonation-dropdown',
  standalone: true,
  imports: [TranslatePipe],
  template: `
    <div class="max-h-80 overflow-y-auto">
      @if (isLoading()) {
        @for (i of [1, 2, 3]; track i) {
          <div class="p-3" style="border-bottom: 1px solid var(--surface-border)">
            <div
              class="h-4 rounded w-3/4 mb-2 animate-pulse"
              style="background-color: var(--surface-200)"
            ></div>
            <div
              class="h-3 rounded w-1/2 animate-pulse"
              style="background-color: var(--surface-100)"
            ></div>
          </div>
        }
      } @else if (users().length > 0) {
        @for (user of users(); track user.id; let i = $index) {
          <div
            class="p-3 cursor-pointer transition-colors duration-150"
            style="border-bottom: 1px solid var(--surface-border)"
            [style.background-color]="hoveredIndex === i ? 'var(--surface-hover)' : 'transparent'"
            (click)="selectUser(user)"
            (mouseenter)="hoveredIndex = i"
            (mouseleave)="hoveredIndex = -1"
            (keydown.enter)="selectUser(user)"
            tabindex="0"
            role="button"
          >
            <div class="font-medium" style="color: var(--text-color)">
              {{ formatUserName(user) }}
            </div>
            <div class="text-sm" style="color: var(--text-color-secondary)">
              {{ user.email }}
            </div>
          </div>
        }
      } @else {
        <div class="p-6 text-center" style="color: var(--text-color-secondary)">
          {{ 'layout.impersonation.noAdminUsers' | translate }}
        </div>
      }
    </div>
  `,
})
export class ImpersonationDropdownComponent implements OnInit {
  private readonly usersService = inject(UsersServiceV2);
  private readonly rolesService = inject(RolesServiceV2);
  private readonly impersonationStore = inject(ImpersonationStore);

  @Input() currentUser: UserProfileDtoV2 | null = null;
  @Output() closed = new EventEmitter<void>();

  readonly users = signal<UserResponseDtoV2[]>([]);
  readonly isLoading = signal(false);
  private readonly adminPermissions = signal<string[]>([]);
  hoveredIndex = -1;

  ngOnInit(): void {
    this.loadAdminUsers();
  }

  private loadAdminUsers(): void {
    this.isLoading.set(true);
    this.usersService
      .apiV2UsersGet(
        undefined, // firstName
        undefined, // lastName
        undefined, // email
        undefined, // isEmailConfirmed
        undefined, // isTwoFactorEnabled
        undefined, // establishmentName
        'Admin', // roleName
        1, // page
        100, // pageSize
        'firstName', // sortBy
        'asc', // sortOrder
        undefined, // search
      )
      .pipe(
        map((response) =>
          (response.data ?? []).filter(
            (user) => !user.isDisabled && !user.roles?.some((r) => r.name === 'SuperAdmin'),
          ),
        ),
        switchMap((users) => {
          // Pre-fetch Admin role permissions from the first user's Admin role
          const adminRole = users[0]?.roles?.find((r) => r.name === 'Admin');
          if (adminRole?.roleId) {
            return this.rolesService.apiV2RolesIdGet(adminRole.roleId).pipe(
              map((role) => {
                this.adminPermissions.set(
                  role.permissions?.map((p) => p.name).filter((n): n is string => !!n) ?? [],
                );
                return users;
              }),
              catchError(() => of(users)),
            );
          }
          return of(users);
        }),
        catchError(() => of([] as UserResponseDtoV2[])),
      )
      .subscribe((users) => {
        this.users.set(users);
        this.isLoading.set(false);
      });
  }

  formatUserName(user: UserResponseDtoV2): string {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  }

  selectUser(user: UserResponseDtoV2): void {
    if (!this.currentUser) return;

    const userProfile: Partial<UserProfileDtoV2> = {
      id: user.id,
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email ?? '',
      roles: user.roles?.map((r) => ({ roleId: r.roleId, name: r.name })) ?? [],
    };

    const roleName = user.roles?.find((r) => r.name === 'Admin')?.name ?? '';

    this.impersonationStore.startImpersonation(
      userProfile as UserProfileDtoV2,
      this.currentUser,
      roleName,
      this.adminPermissions(),
    );
  }
}
